import { useQuery } from '@apollo/client';
import { NotificationsQueryQuery } from '@apps/www/src/__generated__/graphql';
import SVLoadingWrapper from '@apps/www/src/www/containers/SVLoadingWrapper';
import useIsLoggedIn from '@apps/www/src/www/hooks/useIsLoggedIn';
import useNotificationsLimit from '@apps/www/src/www/hooks/useNotificationsLimit';
import NotificationsQuery from '@apps/www/src/www/queries/NotificationsQuery';
import { RootState } from '@apps/www/src/www/reducers';
import { closeNotifications } from '@apps/www/src/www/reducers/ui';
import SVButton from '@pkgs/shared-client/components/SVButton';
import SVCenter from '@pkgs/shared-client/components/SVCenter';
import SVFlexSpacer from '@pkgs/shared-client/components/SVFlexSpacer';
import SVIconButton from '@pkgs/shared-client/components/SVIconButton';
import SVLink from '@pkgs/shared-client/components/SVLink';
import SVNotifications from '@pkgs/shared-client/components/SVNotifications';
import SVRetractableBar from '@pkgs/shared-client/components/SVRetractableBar';
import IconCloseSVG from '@pkgs/shared-client/img/icon-close-inlined.svg';
import NotificationType from '@pkgs/shared/enums/NotificationType';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';
import SVToasterThumbnails from '../components/SVToasterThumbnails';
import { usePendingNotifications } from '../hooks/usePendingNotifications';

type Notification = NotificationsQueryQuery['notifications'][number];

const _PendingNotifications = ({ notification }: { notification: Notification }) => {
	const isBoardInvite = notification.type === NotificationType.INVITE_COLLABORATOR;
	const thumbnails = notification.relatedBoard?.thumbnails;

	const handleContainerClick = (e: React.MouseEvent) => {
		const boardUrl = notification?.relatedBoard?.url || '';
		if (boardUrl) {
			window.location.href = boardUrl;
		}
	};

	return (
		<div
			className={twMerge(
				'bg-primary hover:bg-primary/95 flex cursor-pointer flex-col items-center rounded-[12px] px-3 py-5 shadow-lg ring-1 ring-black/5',
			)}
			onClick={handleContainerClick}
			title={
				isBoardInvite
					? notification.relatedBoard?.name || ''
					: notification.relatedTeam?.name || ''
			}
		>
			<div className={twMerge('flex flex-row items-center space-x-2')}>
				<div className="flex flex-row items-center space-x-4">
					<SVToasterThumbnails
						thumbnails={thumbnails}
						logoURL={notification.relatedTeam?.logoURL || undefined}
						showAvatar={!notification.relatedTeam?.logoURL}
						avatarURL={notification.relatedUser?.avatarURL}
					/>
					<div className="flex flex-1 items-center">
						<div className="w-full">
							<p className="type-label font-medium text-gray-700">
								<>
									<span className="font-semibold text-gray-900">
										{notification.relatedUser?.name ||
											notification.relatedUser?.username}
									</span>
									<span className="text-gray-500">
										{' '}
										invited you to collaborate on{' '}
									</span>
									<span className="font-semibold text-gray-900">
										{notification.relatedBoard?.name ||
											notification.relatedTeam?.name}
									</span>
									<span className="text-gray-500">
										{' '}
										{notification.relatedBoard ? 'board' : 'team'}
									</span>
								</>
							</p>
						</div>
					</div>
				</div>

				<SVButton
					className="type-small text-primary rounded-xl bg-gray-800 px-5 py-3.5 font-medium transition-colors hover:bg-gray-700"
					Component={SVLink}
					to={
						notification.type === NotificationType.INVITE_COLLABORATOR
							? notification.relatedBoard?.acceptURL || ''
							: notification.relatedTeam?.acceptURL || ''
					}
				>
					Accept
				</SVButton>
			</div>
		</div>
	);
};

const _Content = () => {
	const dispatch = useDispatch();
	const limit = useNotificationsLimit();

	const { data, loading, error, refetch } = useQuery(NotificationsQuery, {
		variables: {
			limit,
		},
	});

	const isOpen = useSelector((state: RootState) => state.ui.isNotificationsOpen);

	useEffect(() => {
		if (isOpen) {
			refetch();
		}
	}, [refetch, isOpen]);

	const { notifications: pendingInviteNotifications, hasPendingNotifications } =
		usePendingNotifications();

	const handleClose = () => {
		dispatch(closeNotifications());
	};

	return (
		<div className="z-index-notificationstype-subtitle border-l-separator bg-background -sm:w-screen relative flex h-full w-[400px] flex-col items-center overflow-y-auto border-l">
			<div className="flex w-full items-center p-8 pb-0">
				<h2 className="type-subnav font-semibold">Notifications</h2>
				<SVFlexSpacer />
				<SVIconButton
					onClick={handleClose}
					src={IconCloseSVG}
					label="Close"
					keys="escape"
				/>
			</div>
			{hasPendingNotifications && (
				<div className="w-full space-y-4 px-8 pt-4">
					{pendingInviteNotifications.map((notification) => (
						<_PendingNotifications notification={notification} key={notification._id} />
					))}
				</div>
			)}
			<SVLoadingWrapper isLoading={loading}>
				{() => {
					if (error) {
						return <SVCenter>An error ocurred. Try again later</SVCenter>;
					}

					return (
						<SVNotifications>
							{!data || data.notifications.length === 0 ? (
								<>
									<SVCenter>No notifications yet</SVCenter>
								</>
							) : (
								data.notifications.map((item) => {
									switch (item.type) {
										case NotificationType.NEW_FOLLOWER:
											return (
												<SVNotifications.ItemNewFollower
													key={item._id}
													item={item}
												/>
											);
										case NotificationType.NEW_SAVE:
											return (
												<SVNotifications.ItemNewSave
													key={item._id}
													item={item}
												/>
											);

										case NotificationType.INVITE_COLLABORATOR_ACCEPTED:
										case NotificationType.INVITE_COLLABORATOR_REJECTED:
											return (
												<SVNotifications.ItemInviteCollaboratorAnswer
													key={item._id}
													item={item}
												/>
											);
										case NotificationType.MENTION_IN_COMMENT:
										case NotificationType.NEW_COMMENT:
										case NotificationType.REPLY_TO_COMMENT:
											return (
												<SVNotifications.ItemComment
													key={item._id}
													item={item}
												/>
											);
									}

									return null;
								})
							)}
						</SVNotifications>
					);
				}}
			</SVLoadingWrapper>
		</div>
	);
};

const SVNotificationsContainer = React.memo(() => {
	const dispatch = useDispatch();
	const isOpen = useSelector((state: RootState) => state.ui.isNotificationsOpen);
	const isLoggedIn = useIsLoggedIn();

	const handleClose = () => {
		dispatch(closeNotifications());
	};

	if (!isLoggedIn) {
		return null;
	}

	return (
		<>
			<SVRetractableBar
				position={SVRetractableBar.POSITIONS.RIGHT}
				isOpen={isOpen}
				onClose={handleClose}
				render={() => <_Content />}
			/>
		</>
	);
});

export default SVNotificationsContainer;
